/**
 * PrazoIndicator Component
 *
 * Story: 11.5 - Processos Atribuídos
 * Visual indicator for processo deadline with color coding
 */
import React from "react";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle, Clock, AlertTriangle } from "lucide-react";
import { ProcessoStatus } from "../../types/processo-parceiro";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

export interface PrazoIndicatorProps {
  prazo: string | null;
  status: ProcessoStatus;
  isPrazoVencido: boolean;
  diasAtePrazo: number | null;
  showCountdown?: boolean;
}

const PrazoIndicator: React.FC<PrazoIndicatorProps> = ({
  prazo,
  status,
  isPrazoVencido,
  diasAtePrazo,
  showCountdown = true,
}) => {
  // No prazo set
  if (!prazo) {
    return null;
  }

  // For completed/cancelled processos, show neutral status
  if (status === ProcessoStatus.CONCLUIDO) {
    return (
      <Badge variant="outline" className="border-gray-300 text-gray-600">
        <CheckCircle className="w-3 h-3 mr-1" />
        Concluído
      </Badge>
    );
  }

  if (status === ProcessoStatus.CANCELADO) {
    return (
      <Badge variant="outline" className="border-gray-300 text-gray-600">
        <AlertCircle className="w-3 h-3 mr-1" />
        Cancelado
      </Badge>
    );
  }

  const daysRemaining = diasAtePrazo || 0;
  const prazoDate = new Date(prazo);
  const formattedDate = format(prazoDate, "dd/MM/yyyy");

  // Determine color and icon based on days remaining
  let badgeVariant: "default" | "secondary" | "destructive" | "outline" = "default";
  let Icon = Clock;
  let label = "";
  let className = "";

  if (isPrazoVencido || daysRemaining < 0) {
    // Overdue
    badgeVariant = "destructive";
    Icon = AlertCircle;
    label = `Vencido há ${Math.abs(daysRemaining)} dia${Math.abs(daysRemaining) !== 1 ? "s" : ""}`;
    className = "bg-red-100 text-red-800 border-red-300";
  } else if (daysRemaining === 0) {
    // Due today
    badgeVariant = "destructive";
    Icon = AlertTriangle;
    label = "Vence hoje";
    className = "bg-red-100 text-red-800 border-red-300";
  } else if (daysRemaining <= 3) {
    // 1-3 days (urgent)
    badgeVariant = "outline";
    Icon = AlertTriangle;
    label = `${daysRemaining} dia${daysRemaining !== 1 ? "s" : ""} restante${daysRemaining !== 1 ? "s" : ""}`;
    className = "bg-orange-50 text-orange-700 border-orange-300";
  } else if (daysRemaining <= 7) {
    // 4-7 days (warning)
    badgeVariant = "secondary";
    Icon = Clock;
    label = `${daysRemaining} dias restantes`;
    className = "bg-yellow-50 text-yellow-700 border-yellow-300";
  } else {
    // > 7 days (ok)
    badgeVariant = "outline";
    Icon = Clock;
    label = `${daysRemaining} dias restantes`;
    className = "bg-green-50 text-green-700 border-green-300";
  }

  return (
    <div className="flex items-center gap-2">
      <Badge variant={badgeVariant} className={cn("font-medium", className)}>
        <Icon className="w-3 h-3 mr-1" />
        {showCountdown ? label : formattedDate}
      </Badge>
    </div>
  );
};

export default PrazoIndicator;
